import { ReactNode } from "react";
import { cn } from "../../lib/utils";

type CollapsibleSectionProps = {
  title: string;
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: ReactNode;
  headerAction?: ReactNode;
  boxed?: boolean;
  showToggle?: boolean;
  compact?: boolean;
};

export function CollapsibleSection({
  title,
  count,
  isExpanded,
  onToggle,
  children,
  headerAction,
  boxed = true,
  showToggle = true,
  compact = false,
}: CollapsibleSectionProps) {
  const panelId = `collapsible-${title.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <section
      className={cn(
        "bg-background-card",
        boxed
          ? "border border-brand/40 shadow-sm shadow-brand/20"
          : "border border-transparent bg-transparent shadow-none",
      )}
    >
      <header
        className={cn(
          headerAction
            ? "flex flex-col items-stretch gap-2 md:flex-row md:items-center md:justify-between"
            : "flex items-center justify-between",
          compact ? "px-3 py-2" : "px-4 py-2.5",
          boxed ? "border-b border-border-subtle/60" : "border-b border-border-subtle/30",
        )}
      >
        <div className="flex items-center gap-3">
          {showToggle && (
            <button
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full border transition-transform",
                isExpanded
                  ? "rotate-90 border-brand text-brand"
                  : "border-border-subtle text-foreground-muted hover:border-brand hover:text-brand"
              )}
              onClick={onToggle}
              aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
              aria-expanded={isExpanded}
              aria-controls={panelId}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="h-4 w-4"
              >
                <path
                  fillRule="evenodd"
                  d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
          <h3 className={cn(compact ? "text-lg" : "text-xl", "font-semibold leading-tight text-foreground")}>{title}</h3>
        </div>
        <div className={cn("flex items-center gap-2", headerAction && "w-full md:w-auto")}>
          {headerAction}
          {count !== undefined && (
            <span className={cn(
              "rounded-pill border border-border-subtle/60 font-semibold uppercase tracking-wide text-foreground-muted",
              compact ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-0.5 text-xs",
            )}>
              {count}
            </span>
          )}
        </div>
      </header>

      {isExpanded && (
        <div id={panelId}>
          {children}
        </div>
      )}
    </section>
  );
}
